import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * ReactArtifactPreview Component
 *
 * Renders React component artifacts with:
 * - Live preview using Babel transpilation
 * - Toggle between code view and live preview
 * - Error boundary for compilation/runtime failures
 * - Sandboxed iframe for secure execution
 * - Syntax highlighted code view
 * - Support for state and props
 */
function ReactArtifactPreview({ content, title, isEditing = false, onContentChange }) {
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'code'
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef(null);
  const textareaRef = useRef(null);

  // Switch to edit view when editing starts
  useEffect(() => {
    if (isEditing) {
      setViewMode('edit');
    } else if (viewMode === 'edit') {
      setViewMode('preview');
    }
  }, [isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && viewMode === 'edit' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing, viewMode]);

  // Handle content changes in edit mode
  const handleChange = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
    setError(null); // Clear error on change
  };

  // Calculate line numbers for editor
  const lineCount = (content || '').split('\n').length;

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Refresh the iframe preview
  const handleRefresh = () => {
    setError(null);
    setIframeKey(prev => prev + 1);
  };

  // Extract component name from the code
  const extractComponentName = (code) => {
    // Look for export default function Name or function Name or const Name =
    const patterns = [
      /export\s+default\s+function\s+(\w+)/,
      /export\s+default\s+(\w+)/,
      /function\s+(\w+)\s*\(/,
      /const\s+(\w+)\s*=\s*\(/,
      /const\s+(\w+)\s*=\s*function/,
      /class\s+(\w+)\s+extends/,
    ];

    for (const pattern of patterns) {
      const match = code.match(pattern);
      if (match && match[1] !== 'render') {
        return match[1];
      }
    }
    return 'Component';
  };

  // Process the React code and prepare for iframe
  const processReactCode = (code) => {
    if (!code || !code.trim()) {
      return { error: 'No code provided' };
    }

    // Extract the component name
    const componentName = extractComponentName(code);

    // Clean up the code - remove import/export statements
    let cleanedCode = code
      // Remove import statements (React is already available)
      .replace(/import\s+.*?from\s+['"][^'"]+['"];?\s*/g, '')
      // Remove export default at the end
      .replace(/export\s+default\s+\w+;?\s*$/m, '')
      // Remove inline export default
      .replace(/export\s+default\s+/g, '');

    // Replace common React hooks with React.* equivalents
    // This handles destructured imports like { useState, useEffect }
    const hooks = [
      'useState', 'useEffect', 'useContext', 'useReducer', 'useCallback',
      'useMemo', 'useRef', 'useImperativeHandle', 'useLayoutEffect',
      'useDebugValue', 'useDeferredValue', 'useTransition', 'useId',
      'useSyncExternalStore', 'useInsertionEffect'
    ];

    hooks.forEach(hook => {
      // Replace standalone hook calls (not already prefixed with React.)
      const regex = new RegExp(`(?<!React\\.)\\b${hook}\\b(?=\\s*\\()`, 'g');
      cleanedCode = cleanedCode.replace(regex, `React.${hook}`);
    });

    // Also handle React.Fragment, React.createElement if needed
    cleanedCode = cleanedCode.replace(/(?<!React\.)\bFragment\b/g, 'React.Fragment');

    return { code: cleanedCode, componentName };
  };

  // Generate the HTML document for the iframe
  const generateIframeContent = useMemo(() => {
    if (!content) {
      return '';
    }

    const { code, componentName, error: processError } = processReactCode(content);

    if (processError) {
      return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: system-ui; padding: 20px; color: #ef4444; }
    pre { background: #fef2f2; padding: 12px; border-radius: 8px; overflow-x: auto; }
  </style>
</head>
<body>
  <h3>Error</h3>
  <pre>${processError}</pre>
</body>
</html>`;
    }

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: system-ui, sans-serif;
      margin: 0;
      padding: 16px;
      line-height: 1.5;
      min-height: 100vh;
    }
    #root { min-height: 100%; }
    .error-boundary {
      padding: 16px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #dc2626;
    }
    .error-boundary h3 { margin: 0 0 8px 0; font-size: 16px; }
    .error-boundary pre {
      margin: 0;
      font-size: 13px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  </style>
</head>
<body>
  <div id="root"></div>

  <script type="text/babel" data-presets="react">
    // Error Boundary Component
    class ErrorBoundary extends React.Component {
      constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
      }

      static getDerivedStateFromError(error) {
        return { hasError: true, error };
      }

      componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error('React Error Boundary caught:', error, errorInfo);
        // Notify parent window
        window.parent.postMessage({
          type: 'REACT_ERROR',
          error: error.toString(),
          stack: errorInfo?.componentStack
        }, '*');
      }

      render() {
        if (this.state.hasError) {
          return (
            <div className="error-boundary">
              <h3>Component Error</h3>
              <pre>{this.state.error?.toString()}</pre>
              {this.state.errorInfo && (
                <pre style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          );
        }
        return this.props.children;
      }
    }

    try {
      // User's component code
      ${code}

      // Render the component
      const root = ReactDOM.createRoot(document.getElementById('root'));
      root.render(
        <ErrorBoundary>
          <${componentName} />
        </ErrorBoundary>
      );

      // Notify parent of successful render
      window.parent.postMessage({ type: 'REACT_SUCCESS' }, '*');
    } catch (error) {
      // Handle compilation/initialization errors
      document.getElementById('root').innerHTML = \`
        <div class="error-boundary">
          <h3>Compilation Error</h3>
          <pre>\${error.toString()}</pre>
        </div>
      \`;
      window.parent.postMessage({
        type: 'REACT_ERROR',
        error: error.toString()
      }, '*');
    }
  </script>
</body>
</html>`;
  }, [content]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'REACT_ERROR') {
        setError(event.data.error);
      } else if (event.data?.type === 'REACT_SUCCESS') {
        setError(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar with view toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        {/* View mode toggle */}
        <div className="flex items-center bg-gray-700 rounded-lg p-0.5">
          {isEditing && (
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'edit'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </span>
            </button>
          )}
          <button
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'preview'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </span>
          </button>
          <button
            onClick={() => setViewMode('code')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'code'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Code
            </span>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {viewMode === 'preview' && (
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
                hover:bg-gray-700 rounded transition-colors"
              title="Refresh preview"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
              hover:bg-gray-700 rounded transition-colors"
            title={copied ? 'Copied!' : 'Copy code'}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'edit' ? (
          // Split view - Editor on left, Preview on right
          <div className="flex h-full">
            {/* Code editor panel */}
            <div className="w-1/2 h-full overflow-auto bg-gray-900 border-r border-gray-700">
              <div className="flex h-full">
                {/* Line numbers column */}
                <div className="shrink-0 py-4 pl-4 pr-0 text-right text-gray-500 font-mono text-sm select-none"
                  style={{ minWidth: '3em', lineHeight: '1.6' }}
                >
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i + 1}>{i + 1}</div>
                  ))}
                </div>
                {/* Editable textarea */}
                <textarea
                  ref={textareaRef}
                  value={content || ''}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-gray-100 font-mono text-sm p-4 pl-4 resize-none focus:outline-hidden"
                  style={{
                    lineHeight: '1.6',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    tabSize: 2,
                  }}
                  spellCheck={false}
                  placeholder="// Enter your React component here..."
                />
              </div>
            </div>
            {/* Live preview panel */}
            <div className="w-1/2 h-full p-2 bg-gray-100 dark:bg-gray-900">
              <div className="h-full rounded-lg overflow-hidden bg-white shadow-xs border border-gray-200 dark:border-gray-700">
                <iframe
                  key={content} // Re-render on content change for live preview
                  srcDoc={generateIframeContent}
                  className="w-full h-full"
                  title={title || 'React Preview'}
                  sandbox="allow-scripts"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        ) : viewMode === 'preview' ? (
          // Live preview with sandboxed iframe
          <div className="h-full flex flex-col">
            {error && (
              <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">{error}</span>
                </div>
              </div>
            )}
            <div className="flex-1 p-4 bg-gray-100 dark:bg-gray-900">
              <div className="h-full rounded-lg overflow-hidden bg-white shadow-xs border border-gray-200 dark:border-gray-700">
                <iframe
                  ref={iframeRef}
                  key={iframeKey}
                  srcDoc={generateIframeContent}
                  className="w-full h-full"
                  title={title || 'React Preview'}
                  sandbox="allow-scripts"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        ) : (
          // Code view with syntax highlighting
          <div className="h-full overflow-auto bg-gray-900">
            <SyntaxHighlighter
              language="jsx"
              style={oneDark}
              showLineNumbers={true}
              wrapLines={true}
              lineNumberStyle={{
                minWidth: '3em',
                paddingRight: '1em',
                textAlign: 'right',
                color: '#6b7280',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                background: 'transparent',
                minHeight: '100%',
              }}
              codeTagProps={{
                style: {
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }
              }}
            >
              {content || '// No React code'}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Preview info bar */}
      {viewMode === 'preview' && (
        <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400 flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-cyan-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 10.11c1.03 0 1.87.84 1.87 1.89 0 1-.84 1.85-1.87 1.85S10.13 13 10.13 12c0-1.05.84-1.89 1.87-1.89M7.37 20c.63.38 2.01-.2 3.6-1.7-.52-.59-1.03-1.23-1.51-1.9a22.7 22.7 0 01-2.4-.36c-.51 2.14-.32 3.61.31 3.96m.71-5.74l-.29-.51c-.11.29-.22.58-.29.86.27.06.57.11.88.16l-.3-.51m6.54-.76l.81-1.5-.81-1.5c-.3-.53-.62-1-.91-1.47C13.17 9 12.6 9 12 9c-.6 0-1.17 0-1.71.03-.29.47-.61.94-.91 1.47L8.57 12l.81 1.5c.3.53.62 1 .91 1.47.54.03 1.11.03 1.71.03.6 0 1.17 0 1.71-.03.29-.47.61-.94.91-1.47M12 6.78c-.19.22-.39.45-.59.72h1.18c-.2-.27-.4-.5-.59-.72m0 10.44c.19-.22.39-.45.59-.72h-1.18c.2.27.4.5.59.72M16.62 4c-.62-.38-2 .2-3.59 1.7.52.59 1.03 1.23 1.51 1.9.82.08 1.63.2 2.4.36.51-2.14.32-3.61-.32-3.96m-.7 5.74l.29.51c.11-.29.22-.58.29-.86-.27-.06-.57-.11-.88-.16l.3.51m1.45-7.05c1.47.84 1.63 3.05 1.01 5.63 2.54.75 4.37 1.99 4.37 3.68 0 1.69-1.83 2.93-4.37 3.68.62 2.58.46 4.79-1.01 5.63-1.46.84-3.45-.12-5.37-1.95-1.92 1.83-3.91 2.79-5.38 1.95-1.46-.84-1.62-3.05-1-5.63-2.54-.75-4.37-1.99-4.37-3.68 0-1.69 1.83-2.93 4.37-3.68-.62-2.58-.46-4.79 1-5.63 1.47-.84 3.46.12 5.38 1.95 1.92-1.83 3.91-2.79 5.37-1.95M17.08 12c.34.75.64 1.5.89 2.26 2.1-.63 3.28-1.53 3.28-2.26 0-.73-1.18-1.63-3.28-2.26-.25.76-.55 1.51-.89 2.26M6.92 12c-.34-.75-.64-1.5-.89-2.26-2.1.63-3.28 1.53-3.28 2.26 0 .73 1.18 1.63 3.28 2.26.25-.76.55-1.51.89-2.26m9 2.26l-.3.51c.31-.05.61-.1.88-.16-.07-.28-.18-.57-.29-.86l-.29.51m-2.89 4.04c1.59 1.5 2.97 2.08 3.59 1.7.64-.35.83-1.82.32-3.96-.77.16-1.58.28-2.4.36-.48.67-.99 1.31-1.51 1.9M8.08 9.74l.3-.51c-.31.05-.61.1-.88.16.07.28.18.57.29.86l.29-.51m2.89-4.04C9.38 4.2 8 3.62 7.37 4c-.63.35-.82 1.82-.31 3.96a22.7 22.7 0 012.4-.36c.48-.67.99-1.31 1.51-1.9z" />
          </svg>
          <span>React component running in sandboxed environment with Babel transpilation</span>
        </div>
      )}
    </div>
  );
}

export default ReactArtifactPreview;
